import React, {useMemo, useState} from 'react';
import {
	Dialog,
	DialogFooter,
	DialogType,
	IDialogContentProps,
	PrimaryButton,
	TextField,
} from '@fluentui/react';
import {useVehicleProjectsTranslation} from 'features/VehicleProjects/hooks/vehicleProjectsTranslationHooks';
import {useUpdateBaselineMutation} from '../hooks/baseline.generated';
import {ControlledCheckbox} from 'components/hookForms';
import {Baseline} from 'types';
import {useBaselineTransferForm} from '../hooks/useBaselineTransferForm';

interface Props {
	baselines: Baseline[];
	renameDialogOpen: boolean;
	setRenameDialogOpen: (isOpen: boolean) => void;
}
interface FormValues {
	CodebeamerTransfer: boolean;
}

export function BaselineRenameDialog({
	baselines,
	renameDialogOpen,
	setRenameDialogOpen,
}: Props): JSX.Element {
	const {t} = useVehicleProjectsTranslation({
		keyPrefix: 'BaselineRenameDialog',
	});
	const transferForm = useBaselineTransferForm(false);
	const [updateBaselineMutation] = useUpdateBaselineMutation();

	const [name, setName] = useState('');

	const onTitleChange = (
		event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
		newValue?: string,
	) => {
		setName(newValue ?? '');
	};

	const handleSave = async () => {
		try {
			for (let i = 0; i < baselines.length; i++) {
				// eslint-disable-next-line no-await-in-loop
				await updateBaselineMutation({
					variables: {
						input: {
							id: baselines[i].id,
							name,
							transferWorkflow: {
								transferToCodebeamer: transferForm.getStatus(),
							},
						},
					},
					refetchQueries: [],
				});
			}
			window.location.reload();
		} catch (error) {
			console.error('Failed to rename baseline:', error);
		}

		setRenameDialogOpen(false);
	};

	const handleCancel = async () => {
		setRenameDialogOpen(false);
	};

	const handleOpen = async () => {
		setName(baselines.length === 1 ? baselines[0].name : '');
		transferForm.setFromBaselines(baselines);
	};

	const dialogContentProps: IDialogContentProps = useMemo(() => {
		return {
			type: DialogType.normal,
			title: t('Title'),
			styles: {title: {wordBreak: 'break-word'}},
		};
	}, [t]);
	const checkboxStyles = {root: {paddingLeft: '5px', paddingTop: '5px'}};

	const isNameEmpty = !name;

	return (
		<Dialog
			hidden={!renameDialogOpen}
			onDismiss={handleCancel}
			dialogContentProps={dialogContentProps}
			onLayerDidMount={handleOpen}
		>
			<TextField
				value={name}
				onChange={onTitleChange}
				multiline
				required
				styles={{fieldGroup: {minHeight: '200px'}}}
				errorMessage={isNameEmpty ? t('EmptyNameError') : undefined}
			/>
			<ControlledCheckbox
				name='CodebeamerTransfer'
				label={t('CodebeamerTransfer')}
				control={transferForm.control}
				styles={checkboxStyles}
			/>
			<DialogFooter>
				<PrimaryButton onClick={handleCancel} disabled={false}>
					{t('CancelBtn')}
				</PrimaryButton>

				<PrimaryButton onClick={handleSave} disabled={isNameEmpty}>
					{t('SaveBtn')}
				</PrimaryButton>
			</DialogFooter>
		</Dialog>
	);
}
